import { SearchspringResultsBase, CONSTANTS } from '@theme/searchspring-results-base';
import { compareSearchspringSizeValues } from '@theme/searchspring-core';
import * as filterLogic from '@theme/searchspring-filter-logic';

/**
 * Searchspring Collection component for category/collection pages.
 * Uses bgfilter to pre-filter results by collection handle.
 * @see https://docs.searchspring.com/reference/category-result-pages
 */
class SearchspringCollection extends SearchspringResultsBase {
  static VIRTUAL_RESULTS_PER_PAGE = 100;

  /** Secondary sort key appended to user-applied sorts (see #buildSortQueryWithTiebreaker). */
  static SORT_TIEBREAKER_FIELD = 'title';

  /**
   * Fetches pages 2…totalPages of the request at `url`, in parallel. A page
   * that fails counts as empty — for the page's products and for its list
   * sources alike — so one bad page doesn't lose the rest; the result still
   * shows what arrived, but `complete` tells callers not to cache it. An
   * aborted list request still fails as a whole.
   * @param {string | URL} url - page 1's request URL, without a `page` param
   * @param {number} totalPages
   * @param {AbortSignal} [signal]
   * @returns {Promise<{ results: any[], complete: boolean }>} the results of
   *   pages 2…totalPages, in page order, and whether every page succeeded
   */
  static async #fetchOtherPages(url, totalPages, signal) {
    const pages = await Promise.all(
      Array.from({ length: Math.max(0, totalPages - 1) }, (_, index) => {
        const pageUrl = new URL(url);
        pageUrl.searchParams.set('page', String(index + 2));
        return fetch(pageUrl, { signal })
          .then((response) => (response.ok ? response.json() : { results: [], failed: true }))
          .catch((error) => {
            if (signal?.aborted) throw error;
            return { results: [], failed: true };
          });
      })
    );
    return {
      results: pages.flatMap((page) => page.results || []),
      complete: pages.every((page) => !page.failed),
    };
  }

  /**
   * Beacon scope for this surface, read by searchspring-beacon.js.
   * @type {'category' | 'search'}
   */
  surface = 'category';

  /** @type {string} */
  collectionHandle = '';

  /** @type {string} */
  collectionTitle = '';

  /** @type {string[]} */
  salesStatuses = [];

  /** @type {Set<string>} */
  collectionColors = new Set();

  /** @type {number} */
  perPageLimit = 24;

  /** @type {number} */
  totalProducts = 0;

  /** @type {boolean} */
  virtualPagination = false;

  /** @type {any[]} */
  #allVirtualProducts = [];

  /** @type {Set<string>} */
  #allowedColorCodes = new Set();

  /** @type {Map<string, Set<string>>} */
  #allowedSizesPerColor = new Map();

  /** @type {Set<string>} */
  #allAllowedSizes = new Set();

  /** @type {Map<string, number>} */
  #colorProductCounts = new Map();

  /** @type {Map<string, number>} */
  #colorGroupProductCounts = new Map();

  /** @type {Map<string, number>} */
  #sizeProductCounts = new Map();

  /** @type {{ validColors: Set<string>, sizesPerColor: Map<string, Set<string>>, raw: any }[]} */
  #productValidData = [];

  /** @type {Map<string, Map<string, number>>} field → value → product count */
  #otherFacetCounts = new Map();

  /** @type {Map<string, {codes: Set<string>, sizesPerColor: Map<string, Set<string>>, allSizes: Set<string>, colorCounts: Map<string, number>, groupCounts: Map<string, number>, sizeCounts: Map<string, number>, productData: { validColors: Set<string>, sizesPerColor: Map<string, Set<string>>, raw: any }[], otherFacets: Map<string, Map<string, number>>}>} */
  #allowedColorsCache = new Map();

  /** @type {boolean} */
  #hasUserChangedSorting = false;

  /**
   * Builds the category URL with bgfilter for collection pages.
   * @param {string} siteId
   * @param {string} collectionHandle
   * @param {string} filterQuery
   * @param {string} paginationQuery
   * @param {string} sortQuery
   * @param {string} trackingQuery
   * @param {number} perPage
   * @returns {string}
   */
  static buildCategoryUrl(
    siteId,
    collectionHandle,
    filterQuery,
    paginationQuery,
    sortQuery,
    trackingQuery,
    perPage
  ) {
    const bgfilter = `bgfilter.collection_handle=${encodeURIComponent(collectionHandle)}`;
    return `https://${siteId}.a.searchspring.io/api/search/category.json?siteId=${siteId}&${bgfilter}${filterQuery}&resultsFormat=native&beacon=true&resultsPerPage=${perPage}${paginationQuery}${sortQuery}${trackingQuery}`;
  }

  /**
   * Parse the sales status attribute value.
   * Handles both plain string ("N") and JSON array ('["N","C"]') formats
   * from Shopify metafields.
   * @param {string} raw
   * @returns {string[]}
   */
  static #parseSalesStatuses(raw) {
    const trimmed = raw.trim();
    if (!trimmed) return [];
    if (trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) return parsed.filter((v) => typeof v === 'string' && v);
      } catch {
        // fall through
      }
    }
    return [trimmed];
  }

  /**
   * Parse sales status entries and extract color codes matching any of the given statuses.
   * @param {string[]} entries - e.g. ["N:BLK,WHT", "C:BLU"]
   * @param {string[]} statuses - e.g. ["N", "C"]
   * @returns {string[]} matching color codes
   */
  static parseSalesStatusColors(entries, statuses) {
    if (!entries || !Array.isArray(entries) || statuses.length === 0) return [];
    const statusSet = new Set(statuses);
    const colors = [];
    for (const entry of entries) {
      if (typeof entry !== 'string') continue;
      const colonIndex = entry.indexOf(':');
      if (colonIndex === -1) continue;
      if (!statusSet.has(entry.substring(0, colonIndex))) continue;
      const colorStr = entry.substring(colonIndex + 1);
      if (colorStr) {
        colors.push(...colorStr.split(',').map((c) => c.trim()).filter(Boolean));
      }
    }
    return colors;
  }

  /**
   * Parse the raw mfield_searchspring_price value (HTML-encoded JSON string or object).
   * @param {string | object} raw
   * @returns {Record<string, Record<string, string[]>>}
   */
  static #parseRawPrice(raw) {
    if (!raw) return {};
    if (typeof raw === 'object') return /** @type {Record<string, Record<string, string[]>>} */ (raw);
    if (typeof raw !== 'string') return {};
    try {
      return JSON.parse(raw.replace(/&quot;/g, '"').replace(/&#39;/g, "'"));
    } catch {
      return {};
    }
  }

  /**
   * Normalize a size token to a canonical representation used by filters.
   * Converts to string and trims whitespace. Preserves distinct sizes like "0" and "00".
   * @param {unknown} value
   * @returns {string}
   */
  static #normalizeSizeValue(value) {
    const normalized = String(value ?? '').trim();
    return normalized;
  }

  /**
   * Normalize availability filter values to a canonical product type key.
   * @param {unknown} value
   * @returns {string}
   */
  static #normalizeAvailabilityType(value) {
    const normalized = String(value ?? '').trim().toLowerCase();
    if (!normalized) return '';

    if (
      normalized === CONSTANTS.PRODUCT_TYPES.RTS.toLowerCase()
      || normalized === 'rts'
      || normalized === 'ecom'
    ) {
      return CONSTANTS.PRODUCT_TYPES.RTS;
    }

    if (
      normalized === CONSTANTS.PRODUCT_TYPES.MTO.toLowerCase()
      || normalized === 'mto'
      || normalized === 'made to order'
    ) {
      return CONSTANTS.PRODUCT_TYPES.MTO;
    }

    return '';
  }

  /**
   * Extract color codes and sizes from parsed price data for selected product types.
   * If no type filter is selected, combines RTS and MTO.
   * @param {Record<string, Record<string, string[]>>} priceData
   * @param {Set<string> | null} selectedAvailabilityTypes
   * @returns {{ colors: Set<string>, sizesPerColor: Map<string, Set<string>> }}
   */
  static #extractTypeData(priceData, selectedAvailabilityTypes = null) {
    const typeKeys = selectedAvailabilityTypes && selectedAvailabilityTypes.size > 0
      ? selectedAvailabilityTypes
      : new Set([CONSTANTS.PRODUCT_TYPES.RTS, CONSTANTS.PRODUCT_TYPES.MTO]);

    /** @type {Set<string>} */
    const colors = new Set();
    /** @type {Map<string, Set<string>>} */
    const sizesPerColor = new Map();

    for (const typeKey of typeKeys) {
      const typeData = priceData[typeKey];
      if (!typeData || typeof typeData !== 'object') continue;

      for (const [color, sizeEntries] of Object.entries(typeData)) {
        if (!Array.isArray(sizeEntries) || sizeEntries.length === 0) continue;
        colors.add(color);
        if (!sizesPerColor.has(color)) {
          sizesPerColor.set(color, new Set());
        }
        const sizeSet = /** @type {Set<string>} */ (sizesPerColor.get(color));

        for (const entry of sizeEntries) {
          if (typeof entry !== 'string') continue;
          const sizeStr = SearchspringCollection.#normalizeSizeValue(entry.split(':')[0] || '');
          if (sizeStr && sizeStr !== '*') {
            sizeSet.add(sizeStr);
          }
        }
      }
    }

    return { colors, sizesPerColor };
  }

  async connectedCallback() {
    // Read and validate siteId (no fallback to constants)
    this.siteId = this.dataset.siteId || '';
    if (!this.siteId) {
      console.warn('SearchspringCollection: missing data-site-id attribute');
      return;
    }

    this.collectionHandle = this.dataset.collectionHandle || '';
    this.collectionTitle = this.dataset.collectionTitle || '';
    this.salesStatuses = SearchspringCollection.#parseSalesStatuses(this.dataset.salesStatuses || '');

    // Parse collection color codes whitelist
    const colorsAttr = this.dataset.colors;
    if (colorsAttr) {
      try {
        const parsed = JSON.parse(colorsAttr);
        if (Array.isArray(parsed)) {
          for (const c of parsed) {
            if (typeof c === 'string' && c) this.collectionColors.add(c);
          }
        }
      } catch { /* ignore malformed attribute */ }
    }

    this.perPageLimit = parseInt(this.dataset.perPage, 10) || 24;
    this.totalProducts = parseInt(this.dataset.totalProducts, 10) || 0;
    this.virtualPagination = this.dataset.virtualPagination === 'true';
    this.pagination.perPage = this.perPageLimit;
    this.pagination.defaultPerPage = this.perPageLimit;

    await super.connectedCallback();
  }

  /**
   * Build the sort query with a stable secondary key appended. See
   * buildSortQuery in searchspring-filter-logic.js for why: the category
   * endpoint silently drops a lone `sort.variant_price=asc`, and a second
   * sort key also makes ties deterministic across page boundaries.
   * @returns {string}
   */
  #buildSortQueryWithTiebreaker() {
    return filterLogic.buildSortQuery(this.sorting, SearchspringCollection.SORT_TIEBREAKER_FIELD);
  }

  /**
   * @override
   * @param {Array<{field: string, values: string[]}>} appliedFilters
   * @param {string} trackingQuery
   * @param {number} [page]
   * @returns {string}
   */
  buildApiUrlFor(appliedFilters, trackingQuery, page = this.pagination.currentPage) {
    const filterQuery = SearchspringResultsBase.buildFilterQuery(appliedFilters);
    const paginationQuery = this.virtualPagination
      ? ''
      : SearchspringResultsBase.buildPaginationQuery(page);

    // Per docs: sorting should not be included on initial request for merchandising
    const sortQuery = this.#hasUserChangedSorting
      ? this.#buildSortQueryWithTiebreaker()
      : '';

    const effectivePerPage = this.virtualPagination
      ? SearchspringCollection.VIRTUAL_RESULTS_PER_PAGE
      : this.pagination.perPage;

    return SearchspringCollection.buildCategoryUrl(
      this.siteId,
      this.collectionHandle,
      filterQuery,
      paginationQuery,
      sortQuery,
      trackingQuery,
      effectivePerPage
    );
  }

  /**
   * @override
   * In virtual pagination mode, fetch all pages from Searchspring and aggregate results.
   * @returns {Promise<any[] | null>}
   */
  async fetchProducts() {
    if (!this.virtualPagination) {
      return super.fetchProducts();
    }
    return this.#fetchAllVirtualProducts();
  }

  /**
   * Fetch all pages from Searchspring (max 100 per page), aggregate results,
   * compute filters from the full set, then return a perPage-sized slice.
   * @returns {Promise<any[] | null>} null when a newer fetch superseded this
   *   one — callers then do nothing.
   */
  async #fetchAllVirtualProducts() {
    const fetchId = this.startFetch();
    // Consent can arrive while the pages load and add IDs to the tracking
    // params, so the cache is keyed without them, and each page's request
    // gets the ones current when it's sent.
    const baseUrl = this.buildApiUrl('');
    const canonical = this.cacheKeyFor(this.appliedFilters);
    const listSources = this.fetchListSources();

    if (this.requestCache.has(canonical)) {
      const cached = this.requestCache.get(canonical);
      this.#allVirtualProducts = cached.allProducts;
      this.banners = cached.banners || null;
      this.responseId = cached.responseId || null;
      this.pagination.totalResults = cached.allProducts.length;
      this.pagination.totalPages = this.getDisplayTotalPages(cached.allProducts.length);
      if (this.pagination.currentPage > this.pagination.totalPages) {
        this.pagination.currentPage = 1;
      }
      this.applyFilterData({ facets: cached.facets }, listSources, canonical, fetchId);
      this.updateResultsCount();
      return this.#sliceVirtualPage(this.pagination.currentPage);
    }

    try {
      const prefetched = typeof window !== 'undefined' && window.__ssPrefetch
        ? window.__ssPrefetch[canonical]
        : null;

      let page1Data;
      let fromPrefetch = false;
      if (prefetched) {
        delete window.__ssPrefetch?.[canonical];
        page1Data = await prefetched;
        fromPrefetch = Boolean(page1Data);
      }
      if (!page1Data) {
        const page1Response = await fetch(`${baseUrl}${this.getTrackingParams()}`);
        if (!page1Response.ok) throw new Error(`API request failed: ${page1Response.status}`);
        page1Data = await page1Response.json();
      }

      if (fromPrefetch) {
        await Promise.all([
          customElements.whenDefined('searchspring-search-filters'),
          customElements.whenDefined('searchspring-product-card'),
        ]);
      }

      let allResults = [...(page1Data.results || [])];
      const apiTotalPages = page1Data.pagination?.totalPages || 1;

      // Fetch remaining pages in parallel. A page that failed leaves this
      // short of the full set; everything still displays and feeds the
      // filters below, but the response is only cached when complete (see
      // requestCache.set below) — a partial set cached for the session would
      // stay missing products until the next visit, when it can retry.
      const { results: otherResults, complete } = await SearchspringCollection.#fetchOtherPages(
        `${baseUrl}${this.getTrackingParams()}`,
        apiTotalPages
      );
      allResults.push(...otherResults);

      // A newer filter change started its own fetch while this one waited.
      if (!this.isCurrentFetch(fetchId)) return null;

      const sorting = page1Data?.sorting?.options || [];
      if (sorting.length > 0) {
        this.sortingOptions = sorting;
        this.updateSortingOptions(sorting);
      }

      // Virtual pagination stitches several pages together; the first page's
      // responseId is the one beacon events correlate on.
      this.responseId = page1Data.responseId || null;

      // Merge all results so filter computation sees every product
      const mergedData = { ...page1Data, results: allResults };

      const userPerPage = this.pagination.perPage;
      const userCurrentPage = this.pagination.currentPage;
      this.pagination = { ...page1Data.pagination, perPage: userPerPage, currentPage: userCurrentPage };

      // Compute filters from ALL results
      this.applyFilterData(mergedData, listSources, canonical, fetchId);

      // Extract banners from the first page before slicing: processProducts
      // reserves banner cells on the first page, so this.banners must be set
      // before it runs (see getDisplayWindow).
      const inlineContent = page1Data?.merchandising?.content?.inline;
      /** @type {any[]} */
      const banners = [];
      if (Array.isArray(inlineContent)) {
        for (const item of inlineContent) {
          if (item?.config?.position?.index !== undefined) {
            banners.push({ ...item, positionIndex: item.config.position.index });
          }
        }
        banners.sort((a, b) => a.positionIndex - b.positionIndex);
      }
      this.banners = banners.length > 0 ? banners : null;

      // Process all products (sales status filtering + virtual slicing)
      const productsWithOptions = this.processProducts(allResults);

      this.updateResultsCount();

      // A partial set (see #fetchOtherPages above) would stay missing for the
      // whole visit if cached; not caching it lets the next visit retry.
      if (complete) {
        this.requestCache.set(canonical, {
          allProducts: this.#allVirtualProducts,
          facets: page1Data.facets,
          banners: this.banners,
          responseId: this.responseId,
        });
      }

      return productsWithOptions;
    } catch (error) {
      if (!this.isCurrentFetch(fetchId)) return null;
      console.error('Searchspring API error:', error);
      return [];
    }
  }
  /**
     * @override
     * Collection pages show only the title (server-rendered), no results count.
     */
  updateResultsCount() { }

  /**
   * @override
   * Filter products by sales status after base processing.
   * @param {any[]} products
   * @returns {any[]}
   */
  processProducts(products) {
    const processed = super.processProducts(products).map(product => {
      let mfield_searchspring_product_urls = product.mfield_searchspring_product_urls;
      if (mfield_searchspring_product_urls) {
        // Parse if still an HTML-encoded JSON string
        let parsedUrls = null;
        if (typeof mfield_searchspring_product_urls === 'string') {
          try {
            parsedUrls = JSON.parse(mfield_searchspring_product_urls.replace(/&quot;/g, '"').replace(/&#39;/g, "'"));
          } catch {
            parsedUrls = null;
          }
        }

        if (parsedUrls && typeof parsedUrls === 'object') {
          /** @type {Record<string, any>} */
          const filteredUrls = {};
          for (const [type, colorMap] of Object.entries(parsedUrls)) {
            if (colorMap && typeof colorMap === 'object') {
              /** @type {Record<string, any>} */
              const filteredColorMap = {};
              for (const [color, urls] of Object.entries(colorMap)) {
                filteredColorMap[color] = Array.isArray(urls)
                  ? urls.map((entry) => {
                    if (typeof entry !== 'string') return entry;
                    const colonIndex = entry.indexOf(':');
                    if (colonIndex === -1) return entry;
                    return `${entry.substring(0, colonIndex)}:${this.#addCollectionPrefix(entry.substring(colonIndex + 1))}`;
                  })
                  : urls;
              }
              if (Object.keys(filteredColorMap).length > 0) {
                filteredUrls[type] = filteredColorMap;
              }
            }
          }
          mfield_searchspring_product_urls = filteredUrls;
        }
      }

      return {
        ...product,
        url: this.#addCollectionPrefix(product.url || ''),
        mfield_searchspring_product_urls,
      }
    });

    let filtered;
    if (this.salesStatuses.length > 0) {
      filtered = this.#filterProductsBySalesStatus(processed);
    } else if (this.collectionColors.size > 0) {
      filtered = this.#filterProductsByCollectionColors(processed);
    } else {
      filtered = processed;
    }

    filtered = this.#sortProducts(filtered);

    if (this.virtualPagination) {
      this.#allVirtualProducts = filtered;
      this.pagination.totalResults = filtered.length;
      this.pagination.totalPages = this.getDisplayTotalPages(filtered.length);
      if (this.pagination.currentPage > this.pagination.totalPages) {
        this.pagination.currentPage = 1;
      }
      this.updateResultsCount();
      return this.#sliceVirtualPage(this.pagination.currentPage);
    }

    return filtered;
  }

  /**
   * Slice one display page out of the aggregated virtual product pool. The
   * first page reserves cells for its banners, so later pages are shifted back
   * by that offset (see getDisplayWindow) to keep the stream gap-free.
   * @param {number} page
   * @returns {any[]}
   */
  #sliceVirtualPage(page) {
    const { start, count } = this.getDisplayWindow(page);
    return this.#allVirtualProducts.slice(start, start + count);
  }

  /**
   * Extract the set of color codes still kept on a product after color
   * filtering (sales status / collection colors). Returns null when the
   * product has no color list to constrain the sort price.
   * @param {any} product
   * @returns {Set<string> | null}
   */
  static #getProductAllowedColors(product) {
    if (!Array.isArray(product.mfield_searchspring_colors)) return null;
    /** @type {Set<string>} */
    const codes = new Set();
    for (const item of product.mfield_searchspring_colors) {
      if (typeof item !== 'string') continue;
      const code = item.split(':')[0];
      if (code) codes.add(code);
    }
    return codes.size > 0 ? codes : null;
  }

  /**
   * Find the minimum finite price across the given product types and color
   * whitelist in the raw mfield_searchspring_price data
   * ({ [type]: { [color]: ["size:price:compare", ...] } }).
   * @param {any} product
   * @param {Set<string>} types
   * @param {Set<string> | null} allowedColors
   * @returns {number | null}
   */
  static #minRawPriceForTypes(product, types, allowedColors) {
    const priceData = SearchspringCollection.#parseRawPrice(product.mfield_searchspring_price);
    let min = Infinity;
    for (const type of types) {
      const typeData = priceData[type];
      if (!typeData || typeof typeData !== 'object') continue;
      for (const [color, entries] of Object.entries(typeData)) {
        if (allowedColors && !allowedColors.has(color)) continue;
        if (!Array.isArray(entries)) continue;
        for (const entry of entries) {
          if (typeof entry !== 'string') continue;
          const p = Number(entry.split(':')[1]);
          if (Number.isFinite(p) && p < min) min = p;
        }
      }
    }
    return Number.isFinite(min) ? min : null;
  }

  /**
   * Determine which product type(s) drive the displayed price, mirroring
   * card logic: salesStatuses collections show RTS, an applied availability
   * filter shows the selected type(s), otherwise RTS is the default with
   * MTO as a fallback for products that have no RTS price.
   * @returns {{ primary: Set<string>, fallback: Set<string> | null }}
   */
  #getSortPriceTypes() {
    if (this.salesStatuses.length > 0) {
      return { primary: new Set([CONSTANTS.PRODUCT_TYPES.RTS]), fallback: null };
    }
    const selected = this.#getSelectedAvailabilityTypes();
    if (selected && selected.size > 0) {
      return { primary: selected, fallback: null };
    }
    return {
      primary: new Set([CONSTANTS.PRODUCT_TYPES.RTS]),
      fallback: new Set([CONSTANTS.PRODUCT_TYPES.MTO]),
    };
  }

  /**
   * Sort products by the currently applied sorting. Preserves merchandising
   * order when the user has not changed sorting. For variant_price, sorts by
   * the price of the currently displayed type (RTS for sales-status
   * collections, the selected availability filter when applied, otherwise
   * MTO), restricted to colors still kept on the product.
   * @param {any[]} products
   * @returns {any[]}
   */
  #sortProducts(products) {
    // Only correct over the full result pool. Under server-side pagination this
    // would reorder the current page while page membership still comes from the
    // API, so later pages end up holding items that belong on earlier ones.
    if (!this.virtualPagination) return products;
    if (!this.#hasUserChangedSorting) return products;
    const { field, direction } = this.sorting;
    if (!field || !direction) return products;

    const dir = direction === 'desc' ? -1 : 1;
    const priceTypes = field === 'variant_price' ? this.#getSortPriceTypes() : null;
    const getValue = priceTypes
      ? (/** @type {any} */ p) => {
        const allowedColors = SearchspringCollection.#getProductAllowedColors(p);
        const primary = SearchspringCollection.#minRawPriceForTypes(p, priceTypes.primary, allowedColors);
        if (primary != null) return primary;
        if (priceTypes.fallback) {
          return SearchspringCollection.#minRawPriceForTypes(p, priceTypes.fallback, allowedColors);
        }
        return null;
      }
      : (/** @type {any} */ p) => p[field];

    return products.slice().sort((a, b) => {
      const va = getValue(a);
      const vb = getValue(b);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === 'number' && typeof vb === 'number') {
        return (va - vb) * dir;
      }
      return String(va).localeCompare(String(vb)) * dir;
    });
  }

  /**
   * @override
   * Restricted collections work out their color, group and size lists from
   * the products themselves (see #computeAllowedColors). A cached response
   * has no products, so those lists come back from #allowedColorsCache.
   * @param {any} data
   * @param {string} cacheKey
   */
  prepareFilterData(data, cacheKey) {
    if (!this.#hasColorRestrictions()) return;

    if (data.results) {
      if (this.salesStatuses.length > 0) {
        this.#computeAllowedColors(data);
      } else {
        this.#computeAllowedColorsFromWhitelist(data);
      }
      this.#allowedColorsCache.set(cacheKey, {
        codes: this.#allowedColorCodes,
        sizesPerColor: this.#allowedSizesPerColor,
        allSizes: this.#allAllowedSizes,
        colorCounts: this.#colorProductCounts,
        groupCounts: this.#colorGroupProductCounts,
        sizeCounts: this.#sizeProductCounts,
        productData: this.#productValidData,
        otherFacets: this.#otherFacetCounts,
      });
      return;
    }

    const cached = this.#allowedColorsCache.get(cacheKey);
    if (cached) {
      this.#allowedColorCodes = cached.codes;
      this.#allowedSizesPerColor = cached.sizesPerColor;
      this.#allAllowedSizes = cached.allSizes;
      this.#colorProductCounts = cached.colorCounts;
      this.#colorGroupProductCounts = cached.groupCounts;
      this.#sizeProductCounts = cached.sizeCounts;
      this.#productValidData = cached.productData;
      this.#otherFacetCounts = cached.otherFacets;
    }
  }

  /**
   * @override
   * Drops a list source's prepared allowed-colors data (built from its raw
   * products in prepareFilterData) once its facet has been read and cached
   * elsewhere — that cache key is never looked up again, so pinning the
   * products behind it would just hold memory.
   * @param {string} cacheKey
   */
  forgetPreparedData(cacheKey) {
    this.#allowedColorsCache.delete(cacheKey);
  }

  /**
   * @override
   * Restricted collections work out their lists from products (see
   * prepareFilterData), so their list sources must carry products too.
   * @returns {boolean}
   */
  listSourceNeedsResults() {
    return this.#hasColorRestrictions();
  }

  /**
   * @override
   * A restricted collection without virtual pagination builds its lists from
   * page 1's products under the current sort (fetchListSourceResponse only
   * fetches every page under virtual pagination), so a sort or page-size
   * change must not reuse them.
   * @returns {boolean}
   */
  listsDependOnPaging() {
    return this.#hasColorRestrictions() && !this.virtualPagination;
  }

  /**
   * @override
   * With virtual pagination, a restricted collection works out its lists from
   * every product, so its list sources need every page too. When a later page
   * failed, the response is marked `incomplete: true`: the base class still
   * uses it for this draw, but never caches the facet it produces (see
   * #listFacet), so a later load fetches a full response again.
   * @param {URL} url
   * @param {AbortSignal} [signal]
   * @returns {Promise<any>}
   */
  async fetchListSourceResponse(url, signal) {
    const firstPage = await super.fetchListSourceResponse(url, signal);
    const totalPages = firstPage.pagination?.totalPages || 1;
    if (!this.virtualPagination || !this.#hasColorRestrictions() || totalPages < 2) {
      return firstPage;
    }

    const { results: otherResults, complete } = await SearchspringCollection.#fetchOtherPages(
      url,
      totalPages,
      signal
    );
    const merged = { ...firstPage, results: [...(firstPage.results || []), ...otherResults] };
    return complete ? merged : { ...merged, incomplete: true };
  }

  /**
   * @override
   * Filter color-related facet values to only include colors matching the sales status.
   * @param {any} data
   * @returns {any[]}
   */
  getFilterData(data) {
    const facets = super.getFilterData(data);
    if (!this.#hasColorRestrictions() || this.#allowedColorCodes.size === 0) return facets;
    return this.#filterFacetsBySalesStatus(facets);
  }

  /**
   * Whether any color-level restrictions are active (sales status or collection colors).
   * @returns {boolean}
   */
  #hasColorRestrictions() {
    return this.salesStatuses.length > 0 || this.collectionColors.size > 0;
  }

  /**
   * Compute the set of allowed color codes and sizes from the API response.
   * First filters by sales status (mfield_searchspring_color_sales_status),
    * then intersects with selected availability type data from mfield_searchspring_price
    * (or both RTS + MTO when no availability filter is selected).
   * @param {any} data
   */
  #computeAllowedColors(data) {
    this.#allowedColorCodes = new Set();
    this.#allowedSizesPerColor = new Map();
    this.#allAllowedSizes = new Set();
    this.#colorProductCounts = new Map();
    this.#colorGroupProductCounts = new Map();
    this.#sizeProductCounts = new Map();
    this.#productValidData = [];
    this.#otherFacetCounts = new Map();

    const selectedAvailabilityTypes = this.#getEffectiveAvailabilityTypes();

    const results = data.results || [];
    for (const product of results) {
      // Step 1: Get colors matching the sales status
      const salesStatusColors = new Set(
        SearchspringCollection.parseSalesStatusColors(
          product.mfield_searchspring_color_sales_status,
          this.salesStatuses
        )
      );
      if (salesStatusColors.size === 0) continue;

      // Step 2: Get colors/sizes from selected availability types
      const priceData = SearchspringCollection.#parseRawPrice(product.mfield_searchspring_price);
      const { colors: availableColors, sizesPerColor } = SearchspringCollection.#extractTypeData(
        priceData,
        selectedAvailabilityTypes
      );

      // Step 3: Intersect — colors must match sales status and selected availability type data
      /** @type {Set<string>} */
      const productValidColors = new Set();
      for (const color of availableColors) {
        if (!salesStatusColors.has(color)) continue;
        if (this.collectionColors.size > 0 && !this.collectionColors.has(color)) continue;
        this.#allowedColorCodes.add(color);
        productValidColors.add(color);

        const sizes = sizesPerColor.get(color);
        if (sizes && sizes.size > 0) {
          if (!this.#allowedSizesPerColor.has(color)) {
            this.#allowedSizesPerColor.set(color, new Set());
          }
          const existing = /** @type {Set<string>} */ (this.#allowedSizesPerColor.get(color));
          for (const s of sizes) {
            existing.add(s);
            this.#allAllowedSizes.add(s);
          }
        }
      }

      // Step 4: Update per-color product counts
      for (const color of productValidColors) {
        this.#colorProductCounts.set(color, (this.#colorProductCounts.get(color) || 0) + 1);
      }

      // Step 5: Update per-group product counts
      for (const [group, codes] of Object.entries(this.colorGroupsMap)) {
        if (codes.some(/** @param {string} code */(code) => productValidColors.has(code))) {
          this.#colorGroupProductCounts.set(group, (this.#colorGroupProductCounts.get(group) || 0) + 1);
        }
      }
      // Step 6: Update per-size product counts
      /** @type {Set<string>} */
      const productValidSizes = new Set();
      for (const color of productValidColors) {
        const sizes = sizesPerColor.get(color);
        if (sizes) {
          for (const s of sizes) productValidSizes.add(s);
        }
      }
      for (const size of productValidSizes) {
        this.#sizeProductCounts.set(size, (this.#sizeProductCounts.get(size) || 0) + 1);
      }

      // Step 7: Capture per-product data for cross-filtering
      /** @type {Map<string, Set<string>>} */
      const productSizesMap = new Map();
      for (const color of productValidColors) {
        const colorSizes = sizesPerColor.get(color);
        if (colorSizes) productSizesMap.set(color, colorSizes);
      }
      this.#productValidData.push({ validColors: productValidColors, sizesPerColor: productSizesMap, raw: product });
    }

    // Step 8: Compute counts for non-special facet fields from valid products
    const facets = data.facets || [];
    for (const facet of facets) {
      if (SearchspringCollection.#isSpecialFacetField(facet.field)) continue;
      const counts = new Map();
      for (const { raw } of this.#productValidData) {
        const values = SearchspringCollection.#getProductFacetValues(raw, facet.field);
        for (const v of values) {
          counts.set(v, (counts.get(v) || 0) + 1);
        }
      }
      this.#otherFacetCounts.set(facet.field, counts);
    }
  }

  /**
   * Compute allowed color data from collection color whitelist (no sales status).
   * Iterates all products to find those with matching colors and builds
   * the same data structures as #computeAllowedColors.
   * @param {any} data
   */
  #computeAllowedColorsFromWhitelist(data) {
    this.#allowedColorCodes = new Set();
    this.#allowedSizesPerColor = new Map();
    this.#allAllowedSizes = new Set();
    this.#colorProductCounts = new Map();
    this.#colorGroupProductCounts = new Map();
    this.#sizeProductCounts = new Map();
    this.#productValidData = [];
    this.#otherFacetCounts = new Map();

    const results = data.results || [];
    for (const product of results) {
      /** @type {Set<string>} */
      const productValidColors = new Set();
      if (Array.isArray(product.mfield_searchspring_colors)) {
        for (const item of product.mfield_searchspring_colors) {
          if (typeof item !== 'string') continue;
          const code = item.split(':')[0];
          if (code && this.collectionColors.has(code)) {
            productValidColors.add(code);
            this.#allowedColorCodes.add(code);
          }
        }
      }
      if (productValidColors.size === 0) continue;

      // Extract sizes from price data (all product types) for valid colors
      const priceData = SearchspringCollection.#parseRawPrice(product.mfield_searchspring_price);
      /** @type {Map<string, Set<string>>} */
      const productSizesPerColor = new Map();
      /** @type {Set<string>} */
      const productAllSizes = new Set();

      for (const typeData of Object.values(priceData)) {
        if (!typeData || typeof typeData !== 'object') continue;
        for (const [color, sizeEntries] of Object.entries(/** @type {Record<string, any>} */(typeData))) {
          if (!productValidColors.has(color) || !Array.isArray(sizeEntries)) continue;
          if (!productSizesPerColor.has(color)) productSizesPerColor.set(color, new Set());
          const colorSizes = /** @type {Set<string>} */ (productSizesPerColor.get(color));
          for (const entry of sizeEntries) {
            if (typeof entry !== 'string') continue;
            const sizeStr = SearchspringCollection.#normalizeSizeValue(entry.split(':')[0] || '');
            if (sizeStr && sizeStr !== '*') {
              colorSizes.add(sizeStr);
              this.#allAllowedSizes.add(sizeStr);
              productAllSizes.add(sizeStr);
              if (!this.#allowedSizesPerColor.has(color)) {
                this.#allowedSizesPerColor.set(color, new Set());
              }
              /** @type {Set<string>} */ (this.#allowedSizesPerColor.get(color)).add(sizeStr);
            }
          }
        }
      }

      for (const color of productValidColors) {
        this.#colorProductCounts.set(color, (this.#colorProductCounts.get(color) || 0) + 1);
      }

      for (const [group, codes] of Object.entries(this.colorGroupsMap)) {
        if (codes.some(/** @param {string} code */(code) => productValidColors.has(code))) {
          this.#colorGroupProductCounts.set(group, (this.#colorGroupProductCounts.get(group) || 0) + 1);
        }
      }

      for (const size of productAllSizes) {
        this.#sizeProductCounts.set(size, (this.#sizeProductCounts.get(size) || 0) + 1);
      }

      this.#productValidData.push({ validColors: productValidColors, sizesPerColor: productSizesPerColor, raw: product });
    }

    // Compute other facet counts from valid products
    const facets = data.facets || [];
    for (const facet of facets) {
      if (SearchspringCollection.#isSpecialFacetField(facet.field)) continue;
      const counts = new Map();
      for (const { raw } of this.#productValidData) {
        const values = SearchspringCollection.#getProductFacetValues(raw, facet.field);
        for (const v of values) {
          counts.set(v, (counts.get(v) || 0) + 1);
        }
      }
      this.#otherFacetCounts.set(facet.field, counts);
    }
  }

  /**
   * Fields handled specially by dedicated logic in #filterFacetsBySalesStatus.
   * @param {string} field
   * @returns {boolean}
   */
  static #isSpecialFacetField(field) {
    return field === CONSTANTS.FILTER_FIELDS.COLORS
      || field === CONSTANTS.FILTER_FIELDS.COLOR_GROUPS
      || field === CONSTANTS.FILTER_FIELDS.SIZES
      || field === CONSTANTS.FILTER_FIELDS.AVAILABILITY;
  }

  /**
   * Extract facet values from a raw product for a given facet field.
   * @param {any} product
   * @param {string} field
   * @returns {string[]}
   */
  static #getProductFacetValues(product, field) {
    const raw = product[field];
    if (!raw) return [];
    if (Array.isArray(raw)) return raw.filter((v) => typeof v === 'string');
    if (typeof raw === 'string') return [raw];
    return [];
  }

  /**
   * Compute cross-filtered availability and counts based on selected filters.
   * When colors are selected, sizes are restricted to those available for selected colors.
   * When sizes are selected, colors are restricted to those that have selected sizes.
   * Also recomputes "other" facet counts based on the filtered product set.
   * @param {Set<string> | null} selectedColorCodes
   * @param {Set<string> | null} selectedSizes
   * @returns {{ colorsAvailable: Set<string>, colorCounts: Map<string, number>, groupCounts: Map<string, number>, sizesAvailable: Set<string>, sizeCounts: Map<string, number>, otherFacetCounts: Map<string, Map<string, number>> }}
   */
  #computeCrossFilteredData(selectedColorCodes, selectedSizes) {
    if ((!selectedColorCodes && !selectedSizes) || this.#productValidData.length === 0) {
      return {
        colorsAvailable: this.#allowedColorCodes,
        colorCounts: this.#colorProductCounts,
        groupCounts: this.#colorGroupProductCounts,
        sizesAvailable: this.#allAllowedSizes,
        sizeCounts: this.#sizeProductCounts,
        otherFacetCounts: this.#otherFacetCounts,
      };
    }

    let colorsAvailable = this.#allowedColorCodes;
    let colorCounts = this.#colorProductCounts;
    let groupCounts = this.#colorGroupProductCounts;
    let sizesAvailable = this.#allAllowedSizes;
    let sizeCounts = this.#sizeProductCounts;

    // When colors are selected, recompute available sizes from those colors only
    if (selectedColorCodes) {
      sizesAvailable = new Set();
      sizeCounts = new Map();

      for (const { validColors, sizesPerColor } of this.#productValidData) {
        const matchingColors = [...validColors].filter((c) => selectedColorCodes.has(c));
        if (matchingColors.length === 0) continue;

        /** @type {Set<string>} */
        const productSizes = new Set();
        for (const color of matchingColors) {
          const sizes = sizesPerColor.get(color);
          if (sizes) {
            for (const s of sizes) {
              sizesAvailable.add(s);
              productSizes.add(s);
            }
          }
        }
        for (const s of productSizes) {
          sizeCounts.set(s, (sizeCounts.get(s) || 0) + 1);
        }
      }
    }

    // When sizes are selected, recompute available colors that have those sizes
    if (selectedSizes) {
      colorsAvailable = new Set();
      colorCounts = new Map();
      groupCounts = new Map();

      for (const { validColors, sizesPerColor } of this.#productValidData) {
        /** @type {Set<string>} */
        const productMatchingColors = new Set();

        for (const color of validColors) {
          const sizes = sizesPerColor.get(color);
          if (!sizes) continue;
          let hasMatch = false;
          for (const s of selectedSizes) {
            if (sizes.has(s)) { hasMatch = true; break; }
          }
          if (hasMatch) {
            colorsAvailable.add(color);
            productMatchingColors.add(color);
            colorCounts.set(color, (colorCounts.get(color) || 0) + 1);
          }
        }

        for (const [group, codes] of Object.entries(this.colorGroupsMap)) {
          if (codes.some(/** @param {string} code */(code) => productMatchingColors.has(code))) {
            groupCounts.set(group, (groupCounts.get(group) || 0) + 1);
          }
        }
      }
    }

    // Recompute other facet counts from products matching the selected color/size
    /** @type {Map<string, Map<string, number>>} */
    const otherFacetCounts = new Map();
    for (const { validColors, sizesPerColor, raw } of this.#productValidData) {
      let matches = true;
      if (selectedColorCodes) {
        matches = [...validColors].some((c) => selectedColorCodes.has(c));
      }
      if (matches && selectedSizes) {
        const relevantColors = selectedColorCodes
          ? [...validColors].filter((c) => selectedColorCodes.has(c))
          : [...validColors];
        matches = relevantColors.some((color) => {
          const sizes = sizesPerColor.get(color);
          if (!sizes) return false;
          for (const s of selectedSizes) {
            if (sizes.has(s)) return true;
          }
          return false;
        });
      }
      if (!matches) continue;

      for (const [field, _] of this.#otherFacetCounts) {
        const values = SearchspringCollection.#getProductFacetValues(raw, field);
        if (values.length === 0) continue;
        if (!otherFacetCounts.has(field)) otherFacetCounts.set(field, new Map());
        const fieldCounts = /** @type {Map<string, number>} */ (otherFacetCounts.get(field));
        for (const v of values) {
          fieldCounts.set(v, (fieldCounts.get(v) || 0) + 1);
        }
      }
    }
    // For fields that had no matching products, use empty maps
    for (const field of this.#otherFacetCounts.keys()) {
      if (!otherFacetCounts.has(field)) otherFacetCounts.set(field, new Map());
    }

    return { colorsAvailable, colorCounts, groupCounts, sizesAvailable, sizeCounts, otherFacetCounts };
  }

  /**
   * Filter facet values by allowed Ready to Ship color codes, sizes, and availability.
   * Cross-filters: selecting colors restricts sizes, selecting sizes restricts colors.
   * @param {any[]} facets
   * @returns {any[]}
   */
  #filterFacetsBySalesStatus(facets) {
    const selectedColorCodes = this.#getSelectedColorCodes();
    const selectedSizes = this.#getSelectedSizes();
    const { colorsAvailable, colorCounts, groupCounts, sizesAvailable, sizeCounts, otherFacetCounts } =
      this.#computeCrossFilteredData(selectedColorCodes, selectedSizes);

    return facets.map((facet) => {
      if (facet.field === CONSTANTS.FILTER_FIELDS.COLORS) {
        return {
          ...facet,
          values: facet.values
            .filter(/** @param {any} v */(v) => {
              const colorCode = v.value.split(':')[0];
              return colorsAvailable.has(colorCode);
            })
            .map(/** @param {any} v */(v) => {
              const colorCode = v.value.split(':')[0];
              return { ...v, count: colorCounts.get(colorCode) || 0 };
            })
            .filter(/** @param {any} v */(v) => v.count > 0)
            .sort(/** @param {any} a @param {any} b */(a, b) => b.count - a.count),
        };
      }
      if (facet.field === CONSTANTS.FILTER_FIELDS.COLOR_GROUPS) {
        return {
          ...facet,
          values: facet.values
            .filter(/** @param {any} v */(v) => {
              const groupColors = this.colorGroupsMap[v.value.toLowerCase()] || [];
              return groupColors.some(/** @param {string} code */(code) => colorsAvailable.has(code));
            })
            .map(/** @param {any} v */(v) => ({
              ...v,
              count: groupCounts.get(v.value.toLowerCase()) || 0,
            }))
            .filter(/** @param {any} v */(v) => v.count > 0)
            .sort(/** @param {any} a @param {any} b */(a, b) => b.count - a.count),
        };
      }
      if (facet.field === CONSTANTS.FILTER_FIELDS.AVAILABILITY) {
        if (this.salesStatuses.length > 0) {
          const allowedAvailabilityTypes = new Set([CONSTANTS.PRODUCT_TYPES.RTS]);

          return {
            ...facet,
            values: facet.values.filter(/** @param {any} v */(v) =>
              allowedAvailabilityTypes.has(SearchspringCollection.#normalizeAvailabilityType(v.value))
            ).sort(/** @param {any} a @param {any} b */(a, b) => b.count - a.count),
          };
        }
        return facet;
      }
      if (facet.field === CONSTANTS.FILTER_FIELDS.SIZES) {
        return {
          ...facet,
          values: facet.values
            .filter(/** @param {any} v */(v) => sizesAvailable.has(SearchspringCollection.#normalizeSizeValue(v.value)))
            .map(/** @param {any} v */(v) => ({
              ...v, count: sizeCounts.get(SearchspringCollection.#normalizeSizeValue(v.value)) || 0,
            }))
            .filter(/** @param {any} v */(v) => v.count > 0)
            .sort(/** @param {any} a @param {any} b */(a, b) => compareSearchspringSizeValues(a.value, b.value)),
        };
      }
      // Generic facets: filter to values present in valid (sales-status-filtered) products
      const fieldCounts = otherFacetCounts.get(facet.field);
      if (fieldCounts) {
        return {
          ...facet,
          values: facet.values
            .map(/** @param {any} v */(v) => ({ ...v, count: fieldCounts.get(v.value) || 0 }))
            .filter(/** @param {any} v */(v) => v.count > 0)
            .sort(/** @param {any} a @param {any} b */(a, b) => String(a.value).localeCompare(String(b.value))),
        };
      }
      return facet;
    });
  }

  /**
   * Get the set of color codes currently selected by the user in filters.
   * Returns null if no color or color-group filter is active.
   * @returns {Set<string> | null}
   */
  #getSelectedColorCodes() {
    const colorFilter = this.appliedFilters.find((f) => f.field === CONSTANTS.FILTER_FIELDS.COLORS);
    const groupFilter = this.appliedFilters.find((f) => f.field === CONSTANTS.FILTER_FIELDS.COLOR_GROUPS);

    if (!colorFilter && !groupFilter) return null;

    /** @type {Set<string>} */
    const codes = new Set();

    if (colorFilter) {
      for (const val of colorFilter.values) {
        const code = val.split(':')[0] || '';
        if (code) codes.add(code);
      }
    }

    if (groupFilter) {
      for (const groupName of groupFilter.values) {
        const groupCodes = this.colorGroupsMap[groupName.toLowerCase()] || [];
        for (const code of groupCodes) {
          codes.add(code);
        }
      }
    }

    return codes.size > 0 ? codes : null;
  }

  /**
   * Filter products to only include those that have colors matching the
    * sales status and selected availability type price entries for those colors.
   * When a color filter is active, only keeps products whose valid colors
   * intersect with the selected filter colors.
   * @param {any[]} products
   * @returns {any[]}
   */
  #filterProductsBySalesStatus(products) {
    const selectedColorCodes = this.#getSelectedColorCodes();
    const selectedSizes = this.#getSelectedSizes();
    const selectedAvailabilityTypes = this.#getEffectiveAvailabilityTypes();

    return products
      .filter((product) => {
        const salesStatusColors = SearchspringCollection.parseSalesStatusColors(
          product.mfield_searchspring_color_sales_status,
          this.salesStatuses
        );
        if (salesStatusColors.length === 0) return false;

        const priceData = SearchspringCollection.#parseRawPrice(product.mfield_searchspring_price);
        const typeData = SearchspringCollection.#extractTypeData(priceData, selectedAvailabilityTypes);
        if (typeData.colors.size === 0) return false;

        // Get colors that are both in sales status and selected availability type data
        const validColors = salesStatusColors.filter((color) => {
          if (this.collectionColors.size > 0 && !this.collectionColors.has(color)) return false;
          return typeData.colors.has(color);
        });

        if (validColors.length === 0) return false;

        // If user has selected specific colors, check intersection
        if (selectedColorCodes) {
          const colorsAfterFilter = validColors.filter((color) => selectedColorCodes.has(color));
          if (colorsAfterFilter.length === 0) return false;
          // If user also selected sizes, check that at least one filtered color has a matching size
          if (selectedSizes) {
            return colorsAfterFilter.some((color) => {
              const sizes = typeData.sizesPerColor.get(color);
              if (!sizes) return false;
              for (const s of selectedSizes) {
                if (sizes.has(s)) return true;
              }
              return false;
            });
          }
          return true;
        }

        // If user has selected specific sizes (but no color filter), check sizes
        if (selectedSizes) {
          return validColors.some((color) => {
            const sizes = typeData.sizesPerColor.get(color);
            if (!sizes) return false;
            for (const s of selectedSizes) {
              if (sizes.has(s)) return true;
            }
            return false;
          });
        }

        return true;
      })
      .map((product) => this.#filterProductColors(product));
  }

  /**
   * Filter products to only include those with colors in the collection whitelist.
   * @param {any[]} products
   * @returns {any[]}
   */
  #filterProductsByCollectionColors(products) {
    return products
      .filter((product) => {
        if (Array.isArray(product.mfield_searchspring_colors)) {
          return product.mfield_searchspring_colors.some(/** @param {any} item */(item) => {
            if (typeof item !== 'string') return false;
            return this.collectionColors.has(item.split(':')[0]);
          });
        }
        if (product.searchspringOptions?.colors) {
          return product.searchspringOptions.colors.some(
            /** @param {any} opt */(opt) => this.collectionColors.has(opt.key)
          );
        }
        return false;
      })
      .map((product) => this.#filterProductColors(product, this.collectionColors));
  }

  /**
   * Get the set of sizes currently selected by the user in filters.
   * Returns null if no size filter is active.
   * @returns {Set<string> | null}
   */
  #getSelectedSizes() {
    const sizeFilter = this.appliedFilters.find((f) => f.field === CONSTANTS.FILTER_FIELDS.SIZES);
    if (!sizeFilter) return null;

    /** @type {Set<string>} */
    const sizes = new Set();
    for (const val of sizeFilter.values) {
      const normalized = SearchspringCollection.#normalizeSizeValue(val);
      if (normalized) sizes.add(normalized);
    }
    return sizes.size > 0 ? sizes : null;
  }

  /**
   * Get the effective availability types to use for filtering.
   * When salesStatuses is set, only RTS is allowed regardless of user selection.
   * Otherwise, returns the user-selected availability types (or null).
   * @returns {Set<string> | null}
   */
  #getEffectiveAvailabilityTypes() {
    if (this.salesStatuses.length > 0) {
      return new Set([CONSTANTS.PRODUCT_TYPES.RTS]);
    }
    return this.#getSelectedAvailabilityTypes();
  }

  /**
   * Get selected availability types from filters.
   * Returns null when no availability filter is active.
   * @returns {Set<string> | null}
   */
  #getSelectedAvailabilityTypes() {
    const availabilityFilter = this.appliedFilters.find((f) => f.field === CONSTANTS.FILTER_FIELDS.AVAILABILITY);
    if (!availabilityFilter) return null;

    /** @type {Set<string>} */
    const types = new Set();
    for (const value of availabilityFilter.values) {
      const normalizedType = SearchspringCollection.#normalizeAvailabilityType(value);
      if (normalizedType) types.add(normalizedType);
    }

    return types.size > 0 ? types : null;
  }

  /**
   * Filter a product's color-related data to only include allowed colors.
   * When called without overrideAllowedColors, computes allowed set from
    * sales status + selected availability type data. When provided, uses the override directly.
   * @param {any} product
   * @param {Set<string> | null} [overrideAllowedColors]
   * @returns {any}
   */
  #filterProductColors(product, overrideAllowedColors = null) {
    /** @type {Set<string>} */
    let allowedColors;
    /** @type {Map<string, Set<string>> | null} */
    let sizesPerColor = null;

    if (overrideAllowedColors) {
      allowedColors = overrideAllowedColors;
    } else {
      // Get sales-status colors, then intersect with selected availability type data
      const salesStatusColors = new Set(
        SearchspringCollection.parseSalesStatusColors(
          product.mfield_searchspring_color_sales_status,
          this.salesStatuses
        )
      );
      const priceData = SearchspringCollection.#parseRawPrice(product.mfield_searchspring_price);
      const selectedAvailabilityTypes = this.#getEffectiveAvailabilityTypes();
      const typeResult = SearchspringCollection.#extractTypeData(priceData, selectedAvailabilityTypes);
      sizesPerColor = typeResult.sizesPerColor;

      allowedColors = new Set();
      for (const color of typeResult.colors) {
        if (!salesStatusColors.has(color)) continue;
        if (this.collectionColors.size > 0 && !this.collectionColors.has(color)) continue;
        allowedColors.add(color);
      }
    }

    if (Array.isArray(product.mfield_searchspring_colors)) {
      product.mfield_searchspring_colors = product.mfield_searchspring_colors.filter(
        /** @param {any} item */(item) => {
          const code = typeof item === 'string' ? item.split(':')[0] || '' : '';
          return allowedColors.has(code);
        }
      );
    }

    if (product.searchspringOptions?.colors) {
      product.searchspringOptions.colors = product.searchspringOptions.colors.filter(
        /** @param {any} opt */(opt) => allowedColors.has(opt.key)
      );

      // Update product.images to use the first allowed color's gallery images
      // so that getInitialImages() in the product card shows the correct color.
      const firstAllowedColor = product.searchspringOptions.colors[0];
      if (firstAllowedColor?.images?.length) {
        product.images = firstAllowedColor.images.join('|');
      }
    }

    if (product.searchspringOptions?.sizes) {
      /** @type {Record<string, any>} */
      const filteredSizes = {};
      for (const [color, sizes] of Object.entries(product.searchspringOptions.sizes)) {
        if (!allowedColors.has(color)) continue;
        const allowedSizeSet = sizesPerColor?.get(color);
        if (allowedSizeSet && allowedSizeSet.size > 0) {
          filteredSizes[color] = /** @type {string[]} */ (sizes).filter(
            (/** @type {string} */ s) => allowedSizeSet.has(SearchspringCollection.#normalizeSizeValue(s))
          );
        } else {
          filteredSizes[color] = sizes;
        }
      }
      product.searchspringOptions.sizes = filteredSizes;
    }

    if (product.prices && typeof product.prices === 'object') {
      /** @type {Record<string, any>} */
      const filteredPrices = {};
      for (const [color, priceEntries] of Object.entries(product.prices)) {
        if (!allowedColors.has(color)) continue;
        const allowedSizeSet = sizesPerColor?.get(color);
        if (allowedSizeSet && allowedSizeSet.size > 0 && Array.isArray(priceEntries)) {
          filteredPrices[color] = priceEntries.filter(
            /** @param {any} entry */(entry) => {
              return allowedSizeSet.has(SearchspringCollection.#normalizeSizeValue(entry.size));
            }
          );
        } else {
          filteredPrices[color] = priceEntries;
        }
      }
      product.prices = filteredPrices;
    }

    if (Array.isArray(product.mfield_searchspring_color_availability)) {
      product.mfield_searchspring_color_availability = product.mfield_searchspring_color_availability.filter(
        /** @param {string} item */(item) => {
          const colonIndex = item.indexOf(':');
          const colorCode = colonIndex !== -1 ? item.substring(0, colonIndex) : item;
          return allowedColors.has(colorCode);
        }
      );
    }

    if (product.mfield_searchspring_product_urls) {
      // Parse if still an HTML-encoded JSON string
      let parsedUrls = product.mfield_searchspring_product_urls;
      if (typeof parsedUrls === 'string') {
        try {
          parsedUrls = JSON.parse(parsedUrls.replace(/&quot;/g, '"').replace(/&#39;/g, "'"));
        } catch {
          parsedUrls = null;
        }
      }

      if (parsedUrls && typeof parsedUrls === 'object') {
        /** @type {Record<string, any>} */
        const filteredUrls = {};
        for (const [type, colorMap] of Object.entries(parsedUrls)) {
          if (colorMap && typeof colorMap === 'object') {
            /** @type {Record<string, any>} */
            const filteredColorMap = {};
            for (const [color, urls] of Object.entries(colorMap)) {
              if (allowedColors.has(color)) {
                filteredColorMap[color] = urls;
              }
            }
            if (Object.keys(filteredColorMap).length > 0) {
              filteredUrls[type] = filteredColorMap;
            }
          }
        }
        product.mfield_searchspring_product_urls = filteredUrls;

        // Update product.url to point to the first RTS variant URL
        const rtsUrls = filteredUrls[CONSTANTS.PRODUCT_TYPES.RTS];
        if (rtsUrls && typeof rtsUrls === 'object') {
          const firstColor = Object.keys(rtsUrls)[0];
          if (firstColor) {
            const entries = rtsUrls[firstColor];
            if (Array.isArray(entries) && entries.length > 0 && typeof entries[0] === 'string') {
              const colonIndex = entries[0].indexOf(':');
              if (colonIndex !== -1) {
                product.url = entries[0].substring(colonIndex + 1);
              }
            }
          }
        }
      }
    }

    if (Array.isArray(product.mfield_searchspring_color_groups)) {
      product.mfield_searchspring_color_groups = product.mfield_searchspring_color_groups.filter(
        /** @param {string} item */(item) => {
          const colonIndex = item.indexOf(':');
          const colorCode = colonIndex !== -1 ? item.substring(0, colonIndex) : item;
          return allowedColors.has(colorCode);
        }
      );
    }

    return product;
  }

  /**
   * Prepends /collections/{collectionHandle} to a product URL that starts with /products/.
   * @param {string} url
   * @returns {string}
   */
  #addCollectionPrefix(url) {
    if (!this.collectionHandle || !url.startsWith('/products/')) return url;
    return `/collections/${this.collectionHandle}${url}`;
  }

  /**
   * @override
   * In virtual pagination mode, slice the requested page from the
   * already-fetched product pool instead of making an API call.
   * @param {number} pageNumber
   */
  async goToPage(pageNumber) {
    if (!this.virtualPagination) {
      return super.goToPage(pageNumber);
    }

    if (!Number.isFinite(pageNumber)) return;
    if (pageNumber < 1 || pageNumber > this.pagination.totalPages) return;
    if (pageNumber === this.pagination.currentPage) return;

    this.pagination.currentPage = pageNumber;
    this.updatePaginationUrl();
    this.scrollToGridTop();

    const pageProducts = this.#sliceVirtualPage(pageNumber);
    this.renderProduct(pageProducts);
  }

  /**
   * @override
   * @returns {boolean}
   */
  hasRequiredData() {
    return Boolean(this.collectionHandle);
  }

  /**
   * @override
   * Called when sorting is loaded from URL.
   */
  onSortingLoadedFromUrl() {
    this.#hasUserChangedSorting = true;
  }

  /**
   * @override
   * Called when user changes sorting.
   */
  onUserChangedSorting() {
    this.#hasUserChangedSorting = true;
  }
}

if (!customElements.get('searchspring-collection')) {
  customElements.define('searchspring-collection', SearchspringCollection);
}
